import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { db, auth } from '../lib/firebase';
import { doc, getDoc, updateDoc, deleteDoc, collection, query, where, getDocs, writeBatch } from 'firebase/firestore';
import { sendPasswordResetEmail, deleteUser } from 'firebase/auth';
import { ref as storageRef, deleteObject } from 'firebase/storage';
import { storage } from '../lib/firebase';
import {
  User,
  Mail,
  Phone,
  Shield,
  AlertTriangle,
  LogOut,
  Save,
  Key,
  Trash2,
  Bell,
  Globe,
  Smartphone,
  FileText,
  ExternalLink,
  FolderOpen
} from 'lucide-react';
import toast from 'react-hot-toast';
import { motion } from 'motion/react';

export default function UserSettings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  interface UserDoc {
    key: string;
    label: string;
    url: string;
    storagePath: string;
    firestoreField: string;
    firestoreCollection: string;
    firestoreDoc: string;
    isPrivate: boolean;
  }
  const [userDocs, setUserDocs] = useState<UserDoc[]>([]);
  const [deletingDoc, setDeletingDoc] = useState<string | null>(null);

  interface Contract {
    id: string;
    festivalName: string;
    standName: string;
    format: string;
    paidAt: any;
    contractUrl: string;
  }
  const [contracts, setContracts] = useState<Contract[]>([]);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    organizationName: '',
    phone: '',
    instagram: '',
    linkedin: '',
    language: 'fr',
    timezone: 'Europe/Paris',
    notifMessages: true,
    notifSteps: true,
    notifLogistics: true,
    role: 'festival' as 'festival' | 'stand',
  });

  useEffect(() => {
    async function fetchUserData() {
      if (!user) return;
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setFormData({
            firstName: data.firstName || '',
            lastName: data.lastName || '',
            organizationName: data.organizationName || '',
            phone: data.phone || '',
            instagram: data.instagram || '',
            linkedin: data.linkedin || '',
            language: data.language || 'fr',
            timezone: data.timezone || 'Europe/Paris',
            notifMessages: data.notifMessages ?? true,
            notifSteps: data.notifSteps ?? true,
            notifLogistics: data.notifLogistics ?? true,
            role: data.role || 'festival',
          });
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        toast.error("Erreur lors de la récupération de vos données");
      } finally {
        setLoading(false);
      }
    }

    fetchUserData();
  }, [user]);

  // Charger les contrats (candidatures confirmées avec contractUrl)
  useEffect(() => {
    if (!user) return;
    async function loadContracts() {
      try {
        const q = query(
          collection(db, 'applications'),
          where('applicantId', '==', user!.uid),
          where('status', '==', 'confirme'),
        );
        const snap = await getDocs(q);
        const list: Contract[] = [];
        snap.forEach(d => {
          const data = d.data();
          if (data.contractUrl) {
            list.push({
              id: d.id,
              festivalName: data.festivalName || data.festivalId || '—',
              standName: data.standName || '—',
              format: data.format || '—',
              paidAt: data.paidAt,
              contractUrl: data.contractUrl,
            });
          }
        });
        list.sort((a, b) => (b.paidAt?.seconds || 0) - (a.paidAt?.seconds || 0));
        setContracts(list);
      } catch (e) {
        console.error('Erreur chargement contrats', e);
      }
    }
    loadContracts();
  }, [user]);

  // Load documents based on role
  useEffect(() => {
    if (!user) return;
    async function loadDocs() {
      const role = formData.role;
      const docs: UserDoc[] = [];

      if (role === 'stand') {
        // Public stand fields
        const standSnap = await getDoc(doc(db, 'stands', user!.uid));
        const standData = standSnap.exists() ? standSnap.data() : {};
        // Private documents
        let privData: Record<string, any> = {};
        try {
          const privSnap = await getDoc(doc(db, 'stands', user!.uid, 'private', 'documents'));
          if (privSnap.exists()) privData = privSnap.data();
        } catch {}

        const docDefs = [
          { key: 'insuranceUrl', label: 'Assurance RC Pro', storagePath: `stands/${user!.uid}/doc_assurance`, field: 'insuranceUrl', isPrivate: true },
          { key: 'kbisUrl', label: 'Extrait KBIS', storagePath: `stands/${user!.uid}/doc_kbis`, field: 'kbisUrl', isPrivate: true },
          { key: 'sanitaryUrl', label: 'Certificat sanitaire', storagePath: `stands/${user!.uid}/doc_sanitaire`, field: 'sanitaryUrl', isPrivate: true },
          { key: 'pressKitUrl', label: 'Dossier de presse', storagePath: `stands/${user!.uid}/press_kit`, field: 'pressKitUrl', isPrivate: false },
        ];

        for (const def of docDefs) {
          const url = def.isPrivate ? (privData[def.field] || standData[def.field] || '') : (standData[def.field] || '');
          if (url) {
            docs.push({
              key: def.key,
              label: def.label,
              url,
              storagePath: def.storagePath,
              firestoreField: def.field,
              firestoreCollection: 'stands',
              firestoreDoc: user!.uid,
              isPrivate: def.isPrivate,
            });
          }
        }
      }
      setUserDocs(docs);
    }
    loadDocs();
  }, [user, formData.role]);

  const handleDeleteDoc = async (d: UserDoc) => {
    if (!user) return;
    setDeletingDoc(d.key);
    try {
      // Delete from Firebase Storage
      if (storage) {
        try {
          await deleteObject(storageRef(storage, d.storagePath));
        } catch (err: any) {
          if (err.code !== 'storage/object-not-found') throw err;
        }
      }
      // Delete field from Firestore
      const { deleteField, updateDoc: _updateDoc } = await import('firebase/firestore');
      if (d.isPrivate) {
        await _updateDoc(doc(db, d.firestoreCollection, d.firestoreDoc, 'private', 'documents'), {
          [d.firestoreField]: deleteField(),
        });
        // Also clean main stand doc if present
        try { await _updateDoc(doc(db, d.firestoreCollection, d.firestoreDoc), { [d.firestoreField]: deleteField() }); } catch {}
      } else {
        await _updateDoc(doc(db, d.firestoreCollection, d.firestoreDoc), { [d.firestoreField]: deleteField() });
      }
      setUserDocs(prev => prev.filter(x => x.key !== d.key));
      toast.success(`${d.label} supprimé`);
    } catch (err) {
      console.error(err);
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeletingDoc(null);
    }
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setSaving(false);
    setSaving(true);
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        ...formData,
        updatedAt: new Date().toISOString()
      });
      
      // Update sessionStorage as well so cache doesn't desync on save
      sessionStorage.setItem(`userRole_${user.uid}`, formData.role);
      localStorage.setItem('userRole', formData.role);
      
      toast.success("Paramètres mis à jour !");
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error("Erreur lors de la mise à jour");
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!user?.email) return;
    try {
      await sendPasswordResetEmail(auth, user.email);
      toast.success("E-mail de réinitialisation envoyé !");
    } catch (error) {
      console.error("Error sending reset password:", error);
      toast.error("Erreur lors de l'envoi de l'e-mail");
    }
  };

  const handleDeleteAccount = async () => {
    if (!user) return;
    try {
      const uid = user.uid;
      const batch = writeBatch(db);

      // Cascade : supprimer toutes les données associées
      const collectionsToClean: { col: string; field: string }[] = [
        { col: 'applications', field: 'applicantId' },
        { col: 'notifications', field: 'userId' },
        { col: 'favorites', field: 'userId' },
        { col: 'conversations', field: 'participants' },
      ];

      for (const { col, field } of collectionsToClean) {
        try {
          const q = query(collection(db, col), where(field, field === 'participants' ? 'array-contains' : '==', uid));
          const snap = await getDocs(q);
          snap.docs.forEach(d => batch.delete(d.ref));
        } catch { /* collection peut ne pas exister */ }
      }

      // Profil stand
      batch.delete(doc(db, 'stands', uid));
      // Profil utilisateur
      batch.delete(doc(db, 'users', uid));

      await batch.commit();

      // Supprimer le compte Firebase Auth
      await deleteUser(user);

      sessionStorage.removeItem(`userRole_${uid}`);
      localStorage.removeItem('userRole');

      toast.success('Compte supprimé. À bientôt !');
      navigate('/');
    } catch (error: any) {
      console.error('Error deleting account:', error);
      if (error.code === 'auth/requires-recent-login') {
        toast.error('Reconnectez-vous d\'abord, puis réessayez.');
      } else {
        toast.error('Erreur lors de la suppression du compte.');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#ff5a00]"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 py-12">
      <div className="mb-12">
        <h1 className="text-4xl font-black text-white mb-2">Paramètres</h1>
        <p className="text-zinc-400">Gérez vos informations personnelles, préferences et sécurité.</p>
      </div>

      <div className="space-y-10">
        <form onSubmit={handleUpdateProfile} className="space-y-10">
          {/* BLOC PROFIL */}
          <section className="glass-panel rounded-[2rem] p-8">
            <div className="flex items-center mb-8">
              <div className="w-12 h-12 rounded-2xl bg-[#ff5a00]/10 flex items-center justify-center mr-4">
                <User className="text-[#ff5a00]" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Profil & Contact</h2>
                <p className="text-sm text-zinc-400">Vos informations identitaires</p>
              </div>
            </div>

            <div className="space-y-6">
              {/* Type de compte / Rôle (Exposant vs Organisateur) */}
              <div className="p-6 bg-white/[0.02] border border-white/5 rounded-3xl">
                <label className="block text-xs font-black text-zinc-400 uppercase tracking-widest mb-4">
                  Type de compte actuel (Rôle)
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, role: 'stand' })}
                    className={`flex flex-col items-center justify-center p-4 rounded-2xl border text-center transition-all h-20 cursor-pointer ${
                      formData.role === 'stand'
                        ? 'border-[#ff5a00] bg-[#ff5a00]/10 text-[#ff5a00] font-black shadow-lg shadow-[#ff5a00]/5'
                        : 'border-white/10 bg-white/5 text-zinc-400 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    <span className="text-sm font-black uppercase tracking-wider">EXPOSANT / STAND</span>
                    <span className="text-[9px] opacity-75 font-mono mt-1 uppercase tracking-widest block">Accès candidatures & logistique</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, role: 'festival' })}
                    className={`flex flex-col items-center justify-center p-4 rounded-2xl border text-center transition-all h-20 cursor-pointer ${
                      formData.role === 'festival'
                        ? 'border-[#ff5a00] bg-[#ff5a00]/10 text-[#ff5a00] font-black shadow-lg shadow-[#ff5a00]/5'
                        : 'border-white/10 bg-white/5 text-zinc-400 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    <span className="text-sm font-black uppercase tracking-wider">ORGANISATEUR / FESTIVAL</span>
                    <span className="text-[9px] opacity-75 font-mono mt-1 uppercase tracking-widest block">Création & tri de candidatures</span>
                  </button>
                </div>
                <p className="text-xs text-zinc-500 mt-4 leading-relaxed font-medium">
                  Changez de rôle et enregistrez vos modifications pour basculer instantanément votre espace de travail.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-black text-zinc-500 uppercase tracking-widest mb-2">Prénom</label>
                  <input 
                    type="text" 
                    value={formData.firstName}
                    onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                    className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 focus:outline-none focus:ring-2 focus:ring-[#ff5a00] transition-all font-medium"
                    placeholder="Jean"
                  />
                </div>
                <div>
                  <label className="block text-xs font-black text-zinc-500 uppercase tracking-widest mb-2">Nom</label>
                  <input 
                    type="text" 
                    value={formData.lastName}
                    onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                    className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 focus:outline-none focus:ring-2 focus:ring-[#ff5a00] transition-all font-medium"
                    placeholder="Dupont"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-black text-zinc-500 uppercase tracking-widest mb-2">Nom de l'Organisation (Association, Mairie, Entreprise)</label>
                <div className="relative">
                  <Globe className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                  <input 
                    type="text" 
                    value={formData.organizationName}
                    onChange={(e) => setFormData({...formData, organizationName: e.target.value})}
                    className="w-full bg-white/5 border border-white/10 rounded-2xl pl-14 pr-5 py-4 focus:outline-none focus:ring-2 focus:ring-[#ff5a00] transition-all font-medium"
                    placeholder="Ex: Association Live Nation"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-black text-zinc-500 uppercase tracking-widest mb-2">Téléphone</label>
                <div className="relative">
                  <Phone className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                  <input 
                    type="tel" 
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full bg-white/5 border border-white/10 rounded-2xl pl-14 pr-5 py-4 focus:outline-none focus:ring-2 focus:ring-[#ff5a00] transition-all font-medium"
                    placeholder="06 12 34 56 78"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* LIENS SOCIAUX */}
          <section className="glass-panel rounded-[2rem] p-8">
            <div className="flex items-center mb-8">
              <div className="w-12 h-12 rounded-2xl bg-pink-500/10 flex items-center justify-center mr-4">
                <Globe className="text-pink-500" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Réseaux Sociaux</h2>
                <p className="text-sm text-zinc-400">Renforcez votre visibilité (pour les exposants)</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-xs font-black text-zinc-500 uppercase tracking-widest mb-2">Instagram</label>
                <input 
                  type="text" 
                  value={formData.instagram}
                  onChange={(e) => setFormData({...formData, instagram: e.target.value})}
                  className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 focus:outline-none focus:ring-2 focus:ring-[#ff5a00] transition-all font-medium"
                  placeholder="@mon_compte"
                />
              </div>
              <div>
                <label className="block text-xs font-black text-zinc-500 uppercase tracking-widest mb-2">LinkedIn</label>
                <input 
                  type="text" 
                  value={formData.linkedin}
                  onChange={(e) => setFormData({...formData, linkedin: e.target.value})}
                  className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 focus:outline-none focus:ring-2 focus:ring-[#ff5a00] transition-all font-medium"
                  placeholder="linkedin.com/in/profil"
                />
              </div>
            </div>
          </section>

          {/* GESTION NOTIFICATIONS */}
          <section className="glass-panel rounded-[2rem] p-8">
            <div className="flex items-center mb-8">
              <div className="w-12 h-12 rounded-2xl bg-yellow-500/10 flex items-center justify-center mr-4">
                <Bell className="text-yellow-500" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Notifications</h2>
                <p className="text-sm text-zinc-400">Configurez vos alertes e-mail</p>
              </div>
            </div>

            <div className="space-y-4">
              {[
                { key: 'notifMessages', label: 'Nouveaux messages chat', sub: 'Alertes en temps réel quand on vous contacte' },
                { key: 'notifSteps', label: 'Validation de stands', sub: 'Savoir quand votre candidature est traitée' },
                { key: 'notifLogistics', label: 'Rappels logistiques', sub: 'Briefings, accès et informations terrain' }
              ].map((notif) => (
                <div key={notif.key} className="flex items-center justify-between p-4 bg-white/5 rounded-2xl border border-white/5">
                  <div>
                    <p className="font-bold text-white text-sm">{notif.label}</p>
                    <p className="text-[10px] text-zinc-500 font-medium uppercase tracking-wider">{notif.sub}</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      className="sr-only peer" 
                      checked={(formData as any)[notif.key]}
                      onChange={(e) => setFormData({...formData, [notif.key]: e.target.checked})}
                    />
                    <div className="w-11 h-6 bg-zinc-800 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#ff5a00]"></div>
                  </label>
                </div>
              ))}
            </div>
          </section>

          {/* PRÉFÉRENCES D'AFFICHAGE */}
          <section className="glass-panel rounded-[2rem] p-8">
            <div className="flex items-center mb-8">
              <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 flex items-center justify-center mr-4">
                <Smartphone className="text-emerald-500" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Affichage & Langue</h2>
                <p className="text-sm text-zinc-400">Personnalisez votre interface</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-xs font-black text-zinc-500 uppercase tracking-widest mb-2">{t('settings.interfaceLanguage')}</label>
                <select
                  value={formData.language}
                  onChange={(e) => {
                    const lang = e.target.value;
                    setFormData({ ...formData, language: lang });
                    i18n.changeLanguage(lang);
                    localStorage.setItem('festin_language', lang);
                  }}
                  className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 focus:outline-none focus:ring-2 focus:ring-[#ff5a00] transition-all font-medium text-white"
                >
                  <option value="fr">Français (FR)</option>
                  <option value="en">English (EN)</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-black text-zinc-500 uppercase tracking-widest mb-2">Fuseau horaire</label>
                <select 
                  value={formData.timezone}
                  onChange={(e) => setFormData({...formData, timezone: e.target.value})}
                  className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 focus:outline-none focus:ring-2 focus:ring-[#ff5a00] transition-all font-medium text-white"
                >
                  <option value="Europe/Paris">Europe/Paris (UTC+1)</option>
                  <option value="Europe/London">Europe/London (UTC+0)</option>
                  <option value="America/New_York">America/New_York (UTC-5)</option>
                </select>
              </div>
            </div>
          </section>

          <div className="flex justify-start">
            <button 
              type="submit"
              disabled={saving}
              className="flex items-center justify-center space-x-2 bg-[#ff5a00] hover:bg-[#ff7a33] text-white font-bold py-4 px-10 rounded-2xl transition-all disabled:opacity-50"
            >
              <Save className="w-5 h-5" />
              <span>{saving ? 'Enregistrement...' : 'Sauvegarder tous les réglages'}</span>
            </button>
          </div>
        </form>

        {/* FACTURATION & DOCUMENTS */}
        {/* HISTORIQUE DES CONTRATS */}
        {formData.role === 'stand' && (
          <section className="glass-panel rounded-[2rem] p-8">
            <div className="flex items-center mb-8">
              <div className="w-12 h-12 rounded-2xl bg-[#FF5C00]/10 flex items-center justify-center mr-4">
                <FileText className="text-[#FF5C00]" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Historique des contrats</h2>
                <p className="text-sm text-zinc-400">Vos réservations confirmées et contrats PDF</p>
              </div>
            </div>

            {contracts.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-16 h-16 rounded-2xl bg-white/5 flex items-center justify-center mb-4">
                  <FileText className="w-8 h-8 text-zinc-600" />
                </div>
                <p className="font-bold text-zinc-400 mb-1">Aucun contrat disponible</p>
                <p className="text-sm text-zinc-600">Vos contrats apparaîtront ici après confirmation d'une réservation.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {contracts.map(c => (
                  <div key={c.id} className="flex items-center justify-between p-4 bg-white/5 rounded-2xl border border-white/5 hover:border-white/10 transition-colors">
                    <div className="flex items-center gap-4 min-w-0">
                      <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center shrink-0">
                        <FileText className="w-5 h-5 text-emerald-400" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-bold text-white text-sm truncate">{c.festivalName}</p>
                        <p className="text-xs text-zinc-500">{c.standName} · {c.format}{c.paidAt?.seconds ? ` · ${new Date(c.paidAt.seconds * 1000).toLocaleDateString('fr-FR')}` : ''}</p>
                      </div>
                    </div>
                    <a
                      href={c.contractUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 text-xs font-bold text-emerald-400 hover:text-white bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/20 px-3 py-2 rounded-xl transition-all shrink-0 ml-4"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      <span>Contrat</span>
                    </a>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        <section className="glass-panel rounded-[2rem] p-8">
          <div className="flex items-center mb-8">
            <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center mr-4">
              <FolderOpen className="text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Mes documents</h2>
              <p className="text-sm text-zinc-400">Tous vos fichiers uploadés sur la plateforme</p>
            </div>
          </div>

          {userDocs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-16 h-16 rounded-2xl bg-white/5 flex items-center justify-center mb-4">
                <FileText className="w-8 h-8 text-zinc-600" />
              </div>
              <p className="font-bold text-zinc-400 mb-1">Aucun document uploadé</p>
              <p className="text-sm text-zinc-600">
                {formData.role === 'stand'
                  ? 'Complétez votre profil exposant pour ajouter vos documents (KBIS, assurance, etc.)'
                  : 'Vos documents apparaîtront ici une fois uploadés'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {userDocs.map((d) => (
                <div key={d.key} className="flex items-center justify-between p-4 bg-white/5 rounded-2xl border border-white/5 hover:border-white/10 transition-colors">
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center shrink-0">
                      <FileText className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-bold text-white text-sm truncate">{d.label}</p>
                      <p className="text-[10px] text-emerald-500 font-bold uppercase tracking-wider">Reçu</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 ml-4">
                    <a
                      href={d.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 text-xs font-bold text-zinc-400 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-2 rounded-xl transition-all"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      <span className="hidden sm:inline">Voir</span>
                    </a>
                    <button
                      onClick={() => handleDeleteDoc(d)}
                      disabled={deletingDoc === d.key}
                      className="flex items-center gap-1.5 text-xs font-bold text-red-400 hover:text-white hover:bg-red-500/20 bg-red-500/10 px-3 py-2 rounded-xl transition-all disabled:opacity-50"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      <span className="hidden sm:inline">{deletingDoc === d.key ? '...' : 'Supprimer'}</span>
                    </button>
                  </div>
                </div>
              ))}
              <p className="text-xs text-zinc-600 pt-2">
                Pour ajouter ou remplacer un document, rendez-vous dans la section{' '}
                <button onClick={() => navigate('/my-stand')} className="text-[#FF5C00] hover:underline font-bold">
                  Mon Profil Exposant
                </button>.
              </p>
            </div>
          )}
        </section>

        {/* SÉCURITÉ & SESSIONS */}
        <section className="glass-panel rounded-[2rem] p-8">
          <div className="flex items-center mb-8">
            <div className="w-12 h-12 rounded-2xl bg-blue-500/10 flex items-center justify-center mr-4">
              <Shield className="text-blue-500" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Accès & Sécurité</h2>
              <p className="text-sm text-zinc-400">Authentification et sessions actives</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-6 bg-white/5 rounded-2xl border border-white/5">
              <div>
                <p className="font-bold text-white">Mot de passe</p>
                <p className="text-sm text-zinc-500">Réinitialisez votre mot de passe par e-mail</p>
              </div>
              <button 
                onClick={handlePasswordReset}
                className="flex items-center justify-center space-x-2 bg-white/10 hover:bg-white/20 text-white font-bold py-3 px-6 rounded-xl transition-all"
              >
                <Key className="w-4 h-4" />
                <span>Réinitialiser</span>
              </button>
            </div>

            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-6 bg-blue-500/5 rounded-2xl border border-blue-500/10">
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center mr-4">
                  <Smartphone className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <p className="font-bold text-white">Cet appareil (Actif)</p>
                  <p className="text-[10px] text-zinc-500 uppercase font-black">Navigateur actuel • {new Date().toLocaleDateString()}</p>
                </div>
              </div>
              <button 
                onClick={() => auth.signOut()}
                className="flex items-center justify-center space-x-2 text-zinc-400 hover:text-white text-xs font-black uppercase tracking-widest transition-all"
              >
                <LogOut className="w-4 h-4" />
                <span>Déconnexion</span>
              </button>
            </div>
          </div>
        </section>

        {/* BLOC ZONE DE DANGER */}
        <section className="rounded-[2rem] p-8 border border-red-500/20 bg-red-500/5">
          <div className="flex items-center mb-8">
            <div className="w-12 h-12 rounded-2xl bg-red-500/10 flex items-center justify-center mr-4">
              <AlertTriangle className="text-red-500" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Zone de danger</h2>
              <p className="text-sm text-red-500/60">Actions irréversibles</p>
            </div>
          </div>

          <div className="p-6 bg-black/40 rounded-2xl border border-red-500/10">
            <p className="font-bold text-white mb-2">Supprimer le compte</p>
            <p className="text-sm text-zinc-400 mb-6 font-medium leading-relaxed">
              Une fois votre compte supprimé, toutes vos données (festivals créés, candidatures, stands) seront définitivement effacées. Cette action est irréversible.
            </p>
            
            <button 
              onClick={() => setShowDeleteConfirm(true)}
              className="flex items-center space-x-2 text-red-500 hover:text-red-400 font-bold transition-colors"
            >
              <Trash2 className="w-5 h-5" />
              <span>Supprimer définitivement mon compte</span>
            </button>
          </div>
        </section>
      </div>

      {/* Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-6">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setShowDeleteConfirm(false)} />
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="relative glass-panel p-8 rounded-[2rem] max-w-md w-full shadow-2xl"
          >
            <AlertTriangle className="w-12 h-12 text-red-500 mb-4 mx-auto" />
            <h2 className="text-2xl font-black text-center mb-4">Êtes-vous sûr ?</h2>
            <p className="text-zinc-400 text-center mb-8 font-medium">
              Cette action est définitive. Vous perdrez l'accès à tous vos festivals et stands.
            </p>
            <div className="flex flex-col space-y-3">
              <button 
                onClick={handleDeleteAccount}
                className="w-full bg-red-500 hover:bg-red-600 text-white font-bold py-4 rounded-2xl transition-all"
              >
                Confirmer la suppression
              </button>
              <button 
                onClick={() => setShowDeleteConfirm(false)}
                className="w-full bg-white/5 hover:bg-white/10 text-white font-bold py-4 rounded-2xl transition-all"
              >
                Annuler
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
